import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Mail, Trash2, Copy, Check, Users, Link as LinkIcon } from 'lucide-react';
import { Alert, AlertDescription } from '../ui/alert';

const NewsletterManagement = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedUrl, setCopiedUrl] = useState(false);

  useEffect(() => {
    fetchSubscribers();
  }, []);

  const fetchSubscribers = async () => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/newsletter/subscribers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSubscribers(data.subscribers || []);
      } else {
        console.error('Erreur lors du chargement des abonnés');
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (subscriberId, email) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'abonné ${email} ?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/newsletter/subscribers/${subscriberId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        alert('Abonné supprimé avec succès');
        fetchSubscribers();
      } else {
        alert('Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const copyUnsubscribeUrl = () => {
    const baseUrl = window.location.origin;
    const url = `${baseUrl}/?unsubscribe=true`;
    
    navigator.clipboard.writeText(url).then(() => {
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    });
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header avec statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center">
              <Users className="w-5 h-5 mr-2 text-blue-600" />
              Abonnés Newsletter
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-blue-600">
              {subscribers.length}
            </div>
            <p className="text-sm text-gray-600 mt-2">Abonnés actifs</p>
            
            {/* Bouton copier toutes les adresses */}
            {subscribers.length > 0 && (
              <Button
                onClick={() => {
                  const emails = subscribers.map(sub => sub.email).join(', ');
                  navigator.clipboard.writeText(emails).then(() => {
                    alert(`✅ ${subscribers.length} adresses copiées dans le presse-papiers !\n\nFormat : email1, email2, email3...`);
                  });
                }}
                className="mt-4 w-full bg-blue-600 hover:bg-blue-700"
                size="sm"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copier toutes les adresses
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Card URL de désabonnement */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center">
              <LinkIcon className="w-5 h-5 mr-2 text-amber-600" />
              URL de Désabonnement
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-3">
              Lien à inclure dans vos newsletters pour permettre aux abonnés de se désinscrire :
            </p>
            <div className="flex items-center space-x-2">
              <code className="flex-1 p-2 bg-gray-100 rounded text-sm overflow-x-auto">
                {window.location.origin}/?unsubscribe=true
              </code>
              <Button
                onClick={copyUnsubscribeUrl}
                size="sm"
                className="bg-amber-600 hover:bg-amber-700"
              >
                {copiedUrl ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            {copiedUrl && (
              <p className="text-xs text-green-600 mt-2">✓ URL copiée dans le presse-papiers</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Alert info */}
      <Alert className="bg-blue-50 border-blue-200">
        <Mail className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          <strong>Note :</strong> Les utilisateurs peuvent s'abonner/désabonner directement depuis le formulaire 
          sur la page d'accueil du site (section après les actualités).
        </AlertDescription>
      </Alert>

      {/* Table des abonnés */}
      <Card>
        <CardHeader>
          <CardTitle>Liste des Abonnés</CardTitle>
        </CardHeader>
        <CardContent>
          {subscribers.length === 0 ? (
            <div className="text-center py-12">
              <Mail className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">Aucun abonné pour le moment</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Date d'inscription</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subscribers.map((subscriber) => (
                    <TableRow key={subscriber.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center">
                          <Mail className="w-4 h-4 text-gray-400 mr-2" />
                          {subscriber.email}
                        </div>
                      </TableCell>
                      <TableCell>{formatDate(subscriber.subscribed_at)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          onClick={() => handleDelete(subscriber.id, subscriber.email)}
                          variant="destructive"
                          size="sm"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NewsletterManagement;
